import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

import {cellCursors} from '../to-board-data'
import type {SavedViewsBoardAxisPage} from '../types'
import {
  type BoardAxisLabels,
  axisLabels,
  BoardInitialPayloadSchema,
  type BoardPayloadItem,
  toBoardAxisValues,
  toBoardCell,
} from './board-payload'

const ColumnSchema = BoardInitialPayloadSchema.properties.columns.items
const CellSchema = BoardInitialPayloadSchema.properties.cells.items
const ItemSchema = CellSchema.properties.items.items
const PageCellSchema = Type.Object({
  ...CellSchema.properties,
  itemsLoaded: Type.Boolean(),
  itemsCursor: Type.Union([Type.String(), Type.Null()]),
})

const CellPageSchema = Type.Object({
  columnKey: Type.String(),
  rowKey: Type.String(),
  items: Type.Array(ItemSchema),
  hasMoreItems: Type.Boolean(),
  itemsCursor: Type.Union([Type.String(), Type.Null()]),
})
export type BoardCellPage = Type.Static<typeof CellPageSchema>

const boardCellPageValidator = Compile(CellPageSchema)

/**
 * A cursor is minted exactly when there is more to reach, and an empty string is not one: the
 * server reads a blank cursor as no cursor at all, so a direction that accepted one would ask
 * for its own first page again for as long as the reader kept reaching the end of it.
 */
function statesCursor(cursor: string | null, hasMore: boolean): boolean {
  return hasMore ? cursor !== null && cursor !== '' : cursor === null
}

export function isBoardCellPage(value: unknown): value is BoardCellPage {
  if (!boardCellPageValidator.Check(value)) return false

  return statesCursor(value.itemsCursor, value.hasMoreItems)
}

const AxisPageSchema = Type.Object({
  axis: Type.Union([Type.Literal('columns'), Type.Literal('rows')]),
  values: Type.Array(ColumnSchema),
  hasMoreValues: Type.Boolean(),
  cursor: Type.Union([Type.String(), Type.Null()]),
  truncated: Type.Boolean(),
  cells: Type.Array(PageCellSchema),
  /** Axis values per page. Not the initial payload's `pageSize`, which is items per cell. */
  axisPageSize: Type.Number(),
})
export type BoardAxisPage = Type.Static<typeof AxisPageSchema>

const boardAxisPageValidator = Compile(AxisPageSchema)

export function isBoardAxisPage(value: unknown): value is BoardAxisPage {
  if (!boardAxisPageValidator.Check(value)) return false

  return (
    statesCursor(value.cursor, value.hasMoreValues) &&
    value.cells.every(
      cell =>
        statesCursor(cell.itemsCursor, cell.hasMoreItems) &&
        (cell.itemsLoaded || (cell.items.length === 0 && !cell.hasMoreItems)),
    )
  )
}

export function toBoardAxisPage(
  page: BoardAxisPage,
  fieldLabel: string | null | undefined,
): SavedViewsBoardAxisPage<BoardPayloadItem, BoardAxisLabels> {
  return {
    axis: page.axis,
    values: toBoardAxisValues(page.values, fieldLabel),
    hasMoreValues: page.hasMoreValues,
    cursor: page.cursor,
    truncated: page.truncated,
    cells: page.cells.map(toBoardCell),
    cellCursors: cellCursors(page.cells),
    metadata: axisLabels(page.values),
  }
}
